import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as customerRiskAndCoverageActionCreators from '@makemydeal/dr-dash-store/actions/customerRiskAndCoverageActionCreators';
import * as complianceSelectors from '@makemydeal/dr-dash-store/selectors/complianceSelectors';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';

import { useDispatch, useSelector } from 'react-redux';
import type { ComplianceSections } from '../../shared/types';
import { ComplianceStatus } from '../../shared/types';
import { useState, useEffect } from 'react';
import { COMPLIANCE_ALERT_MESSAGES } from '../../shared/constants';
import type { ComplianceAlertProps } from './ComplianceAlert';
import { ComplianceAlert } from './ComplianceAlert';
import { VppDealType } from '@makemydeal/dr-platform-types';

export const ComplianceBannerAlert = () => {
    const dispatch = useDispatch();
    const shouldShowComplianceElements = useSelector(complianceSelectors.shouldShowComplianceElements);
    const participantMatch = useSelector(complianceSelectors.getComplianceParticipantForShopper);
    const [complianceDetails, setComplianceDetails] = useState<ComplianceSections | null>(null);
    const { dealType } = useSelector(vehicleProtectionSelectors.getVppSurchargesOverrides);

    useEffect(() => {
        if (shouldShowComplianceElements && participantMatch) {
            setComplianceDetails(participantMatch.compliance || null);
        }
    }, [shouldShowComplianceElements, participantMatch]);

    function hasAnyErrors(data: ComplianceSections): boolean {
        const sectionsToCheck = ['OFAC', 'redFlags'] as const;
        return sectionsToCheck.some((key) => {
            const section = data[key];
            return (
                section &&
                ((Array.isArray(section.errors) && section.errors.length > 0) || section.status === ComplianceStatus.ERROR)
            );
        });
    }
    const getAlertProps = (): ComplianceAlertProps | null => {
        if (!shouldShowComplianceElements || !complianceDetails || hasAnyErrors(complianceDetails)) {
            return null;
        }

        const OFACStatus = complianceDetails.OFAC?.status;
        const redFlagsStatus = complianceDetails.redFlags?.status;

        if (!OFACStatus && !redFlagsStatus) {
            return null;
        }
        if (OFACStatus === ComplianceStatus.FAIL || redFlagsStatus === ComplianceStatus.FAIL) {
            return { type: 'error', message: COMPLIANCE_ALERT_MESSAGES.FAIL };
        }

        if (
            dealType === VppDealType.CASH &&
            OFACStatus === ComplianceStatus.PASS &&
            redFlagsStatus === ComplianceStatus.NOT_APPLICABLE
        ) {
            return { type: 'success', message: COMPLIANCE_ALERT_MESSAGES.PASS };
        }

        if (OFACStatus === ComplianceStatus.PASS && redFlagsStatus === ComplianceStatus.PASS) {
            return { type: 'success', message: COMPLIANCE_ALERT_MESSAGES.PASS };
        }
        return null;
    };

    const alertProps = getAlertProps();
    useEffect(() => {
        if (alertProps?.type === 'error') {
            dispatch(baseDealActionCreators.setCustomerRiskAndCoverageTab('compliance'));
            dispatch(baseDealActionCreators.setCustomerRiskAndCoverageSlideOutStatus(true));
            dispatch(customerRiskAndCoverageActionCreators.customerRiskAndCoverageSlideOutButton());
        }
    }, [alertProps?.type, dispatch]);

    if (!alertProps) {
        return null;
    }

    return <ComplianceAlert {...alertProps} />;
};
